/**
 * หน้า "ออร์เดอร์ของฉัน" — logic ล้วน (node --test) · ห้าม import env.ts (ใช้ import.meta.env)
 * ข้อมูลมาจาก Cloud Function memberApi (admin repo: src/member/memberOrdersQuery.js)
 */

/** offering = ถวายทรัพย์ (ใบบุญ DN — เปิดหน้า ?view=offering) */
export type MemberOrderType = 'product' | 'ceremony' | 'offering'
export type MemberAction = 'pay' | 'fill' | null
export type PillTone = 'pay' | 'fill' | 'review' | 'ship' | 'done' | 'cancel'

export interface MemberOrderItem {
  name: string
  qty: number
  price: number
}

export interface MemberDeadline {
  kind: 'created' | 'paid_confirm'
  dueAt: number
  cancelAt: number
  warnAt: number
}

export interface MemberOrder {
  orderId: string
  orderType: MemberOrderType
  date: string
  sortTs: number
  status: string
  group: 'active' | 'history'
  actionNeeded: MemberAction
  /** Optional for caches/API responses from before deadline support. */
  deadline?: MemberDeadline | null
  items: MemberOrderItem[]
  total: number
  trackingNumber: string
  carrier: string
  trackingUrl: string
  botId: string
  /** รหัสลับลิงก์ออร์เดอร์ (&k=) — ไม่มี = ว่าง (API/แคชเก่า) · ใช้ต่อท้ายลิงก์ตอนเปิดออร์เดอร์จากรายการ */
  linkKey?: string
  /** 0 สั่ง · 1 ชำระ · 2 รอจัดส่ง/รอประกอบพิธี · 3 สำเร็จ/เสร็จพิธี · -1 ยกเลิก */
  step: number
}

export interface MemberOrdersResponse {
  ok: boolean
  benefitsEligible?: boolean
  membership?: {
    tier: { id: string; name: string; icon: string; color: string }
    nextTier: { id: string; name: string; icon: string } | null
    toNextTierBaht: number
    toNextTierPoints?: number
    points: number
  }
  birthdayOffer?: {
    year: number
    title: string
    description: string
    eligible: boolean
    needsBirthday: boolean
    claimStatus: 'pending' | 'fulfilled' | null
  }
  luckyCampaign?: { title: string; message: string }
  /** เทวาลัยออนไลน์: เลขมงคลล่าสุด (จุดธูปที่จ่ายแล้วภายใน 14 วัน) — ไม่มี = ไม่แสดงแถบ */
  templeLatest?: { playId: string; digits3: string[]; digits2: string[]; paidAt: string }
  /** มีเฉพาะโหมดแอดมินดูมุมมองลูกค้า (listCustomerOrders) */
  viewAs?: { orderId: string; displayName: string }
  profile?: { displayName: string; pictureUrl: string }
  memberProfile?: {
    recipientName: string
    phone: string
    address: string
    birthday: string
    needsSetup: boolean
    source: string
  }
  activities?: MemberActivity[]
  /** มีกิจกรรมที่บัญชีนี้ยังไม่เคยเห็น → ถุงทองเด้ง */
  activitiesHasNew?: boolean
  /** ป้ายประชาสัมพันธ์ที่ควรขึ้น (คัดฝั่ง server แล้ว) */
  announcements?: Array<{
    id: string
    version: number
    title: string
    body: string
    cover?: { preset: 'shipping' | 'ceremony' | 'welcome' | 'promo' | 'gold' | 'cloth' | ''; imageUrl: string }
    buttons: Array<{ label: string; kind: 'orders' | 'line' | 'link' | 'close'; value: string }>
    dismissMode: 'optout' | 'daily' | 'forever'
  }>
  summary?: { totalOrders: number; lifetimeTotal: number }
  orders?: MemberOrder[]
  error?: string
  message?: string
}

export interface MemberActivity {
  id: string
  version?: number
  name: string
  description: string
  keywords: string[]
  startAt: number | null
  endAt: number | null
  prizeRemaining?: number
  prizeTotal?: number
  /** ถุงทอง (memberApi ต.ค. 2569) — API เก่าไม่มีฟิลด์เหล่านี้ */
  cover?: { preset: 'shipping' | 'ceremony' | 'welcome' | 'promo' | 'gold' | 'cloth' | ''; imageUrl: string }
  distribution?: 'fcfs' | 'random'
  isNew?: boolean
}

/** หน้าแรกแสดงคำแนะนำ; ?view=orders เข้ารายการสมาชิก; orderId เปิดออร์เดอร์เดี่ยว */
export function readUrlView(search: string): 'landing' | 'orders' | null {
  try {
    const sp = new URLSearchParams(search)
    if (sp.get('view') === 'orders') return 'orders'
    let state = sp.get('liff.state')
    if (state && /%3[fd]/i.test(state)) state = decodeURIComponent(state)
    if (state?.startsWith('?')) state = state.slice(1)
    const stateParams = new URLSearchParams(state || '')
    if (stateParams.get('view') === 'orders') return 'orders'
    if (sp.has('orderId') || stateParams.has('orderId') || sp.has('view') || stateParams.has('view')) return null
    return 'landing'
  } catch {
    return null
  }
}

/** ?as=ORDERID (โหมดแอดมินดูมุมมองลูกค้า) — ตรงหรือใน liff.state · เก็บเฉพาะ A-Z a-z 0-9 - */
export function readUrlAs(search: string): string | null {
  try {
    const sp = new URLSearchParams(search)
    let raw = sp.get('as')
    if (!raw) {
      let state = sp.get('liff.state')
      if (!state) return null
      if (/%3[fd]/i.test(state)) state = decodeURIComponent(state)
      if (state.startsWith('?')) state = state.slice(1)
      raw = new URLSearchParams(state).get('as')
    }
    const id = String(raw || '').replace(/[^a-zA-Z0-9-]/g, '')
    return id || null
  } catch {
    return null
  }
}

export function splitByTab(orders: MemberOrder[], now = Date.now()): { active: MemberOrder[]; history: MemberOrder[] } {
  const urgentAt = (order: MemberOrder) => {
    const deadline = orderDeadline(order)
    return deadline && now >= deadline.warnAt ? deadline.cancelAt : Infinity
  }
  return {
    active: orders.filter((o) => o.group === 'active').sort((a, b) => {
      const first = urgentAt(a)
      const second = urgentAt(b)
      return first === second ? 0 : first < second ? -1 : 1
    }),
    history: orders.filter((o) => o.group !== 'active'),
  }
}

export function statusPill(o: MemberOrder): { label: string; tone: PillTone } {
  if (o.step < 0) return { label: 'ยกเลิก', tone: 'cancel' }
  if (o.actionNeeded === 'pay') return { label: 'รอชำระเงิน', tone: 'pay' }
  if (o.actionNeeded === 'fill') return { label: 'รอกรอกข้อมูล', tone: 'fill' }
  if (o.step === 3) return { label: o.status || 'สำเร็จ', tone: 'done' }
  if (o.step === 2) return { label: o.status || 'รอจัดส่ง', tone: 'ship' }
  return { label: o.status || '-', tone: 'review' }
}

export function actionLabel(o: MemberOrder): string | null {
  if (o.group !== 'active' || o.step < 0) return null
  if (o.orderType === 'offering') return o.actionNeeded === 'pay' ? 'ชำระถวายทรัพย์' : 'ดูรายละเอียด'
  if (o.actionNeeded === 'pay') return 'ชำระเงินบูชา'
  if (o.actionNeeded === 'fill') return o.orderType === 'ceremony' ? 'กรอกข้อมูลผู้ร่วมพิธี' : 'ตรวจสอบและยืนยันข้อมูลจัดส่ง'
  if (o.trackingNumber) return 'ดูการจัดส่ง'
  return 'ดูรายละเอียดออร์เดอร์'
}

/** Discard incompatible or invalid data so old caches cannot show false warnings. */
export function orderDeadline(o: MemberOrder): MemberDeadline | null {
  const d = o.deadline
  if (o.group !== 'active' || !o.actionNeeded || !d || o.step < 0) return null
  const expectedStatus = d.kind === 'created' ? 'สร้างออร์เดอร์' : d.kind === 'paid_confirm' ? 'ชำระเงินสำเร็จ' : null
  if (!expectedStatus || o.status !== expectedStatus) return null
  if (![d.dueAt, d.cancelAt, d.warnAt].every((value) => Number.isFinite(value) && value > 0)) return null
  return d.cancelAt >= d.dueAt && d.warnAt < d.cancelAt ? d : null
}

const deadlineDateFormat = new Intl.DateTimeFormat('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })

export function deadlineNotice(o: MemberOrder, now: number): { due: string; warning: string } | null {
  const d = orderDeadline(o)
  if (!d) return null
  const date = deadlineDateFormat.format(d.dueAt)
  const due = `กรุณา${o.actionNeeded === 'pay' ? 'ชำระ' : 'ยืนยันข้อมูล'}ภายใน ${date} น.`
  const remaining = d.cancelAt - now
  if (remaining <= 0) return { due, warning: 'ครบกำหนดยกเลิกแล้ว กรุณาเปิดออร์เดอร์เพื่อตรวจสอบสถานะ' }
  if (now < d.warnAt) return { due, warning: '' }
  const minutes = Math.ceil(remaining / 60_000)
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  const time = hours ? `${hours} ชั่วโมง${rest ? ` ${rest} นาที` : ''}` : `${minutes} นาที`
  return { due, warning: `เหลืออีก ${time} ก่อนยกเลิกอัตโนมัติ` }
}

export function orderCardTone(o: MemberOrder): PillTone | 'ceremony' | 'archive' {
  if (o.status === 'สร้างออร์เดอร์') return 'pay'
  if (o.status === 'ชำระเงินสำเร็จ') return 'fill'
  if (o.status === 'รอประกอบพิธี' || o.status === 'เสร็จพิธี') return 'ceremony'
  if (o.status === 'รอจัดเก็บ') return 'archive'
  return statusPill(o).tone
}

export function stepLabels(orderType: MemberOrderType): string[] {
  if (orderType === 'offering') return ['ร่วมถวาย', 'ชำระ', 'ตรวจสอบ', 'อนุโมทนา']
  return orderType === 'ceremony'
    ? ['ลงทะเบียน', 'ชำระ', 'รอประกอบพิธี', 'เสร็จพิธี']
    : ['สั่ง', 'ชำระ', 'รอจัดส่ง', 'สำเร็จ']
}

const TH_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']

export function formatShortDate(o: Pick<MemberOrder, 'sortTs' | 'date'>): string {
  if (o.sortTs > 0) {
    const d = new Date(o.sortTs + 7 * 3600_000)
    return `${d.getUTCDate()} ${TH_MONTHS[d.getUTCMonth()]}`
  }
  return o.date || '-'
}

export function formatBaht(n: number): string {
  return `฿${Math.round(Number(n) || 0).toLocaleString('en-US')}`
}

/** ปุ่ม 📋 บนหัวหน้าออร์เดอร์ — เฉพาะเจ้าของออร์เดอร์ที่ได้โปรไฟล์จาก LIFF จริง */
export function shouldShowMyOrdersLink(i: {
  lineVerified: boolean
  profileUserId: string
  orderLineUserId: string
  minimal: boolean
}): boolean {
  if (i.minimal || !i.lineVerified) return false
  const p = String(i.profileUserId || '').trim()
  const o = String(i.orderLineUserId || '').trim()
  return !!p && p === o
}

/**
 * ปุ่มบนหัวหน้าออร์เดอร์: owner = 📋 ออร์เดอร์ของตัวเอง · admin = 👁 มุมมองลูกค้า (LINE แอดมินในรายชื่อ เปิดออร์เดอร์คนอื่น)
 * isAdminViewer มาจาก memberApi viewerStatus — สิทธิ์จริงตรวจซ้ำที่ CF ทุกครั้ง (ปุ่มเป็นแค่ทางเข้า)
 */
export function myOrdersLinkMode(i: {
  lineVerified: boolean
  profileUserId: string
  orderLineUserId: string
  minimal: boolean
  isAdminViewer: boolean
  /** LINE Desktop redirect มี liff.state ของออร์เดอร์นี้ แม้ไม่มี LIFF in-app profile */
  lineOrderEntry?: boolean
  /** เปิดในแอป LINE (LIFF) — ถือว่า login แล้ว */
  inLineApp?: boolean
  /** เปิดจากเว็บแต่ยัง login LINE อยู่ (ธง login ที่ยังสด) */
  webLoggedIn?: boolean
}): 'owner' | 'admin' | null {
  if (i.minimal) return null
  if (shouldShowMyOrdersLink(i)) return 'owner'
  if (i.lineVerified && i.profileUserId.trim()) {
    if (!i.orderLineUserId.trim()) return 'owner'
    return i.isAdminViewer ? 'admin' : null
  }
  // เว็บที่ไม่ได้ login = ไม่แสดง (กดแล้วไม่มีหน้าให้ไป และห้ามพาไป LINE โดยไม่ตั้งใจ)
  return i.lineOrderEntry || i.inLineApp || i.webLoggedIn ? 'owner' : null
}

/**
 * เปิด /?view=orders จากเบราว์เซอร์ภายนอกโดยไม่มี login ที่ยังสด และไม่ได้กดเข้าสู่ระบบเอง
 * (เช่น URL เก่าในประวัติ/แท็บที่กู้คืน/ออกจากระบบแล้ว) → แสดงหน้าแรกแทนการเด้งไป LINE login
 * ไม่ครอบคลุม: ในแอป LINE · กำลังกลับจาก LINE (code/state) · ลูกค้ากดปุ่มเข้าสู่ระบบ (login=1)
 */
export function shouldShowLandingInsteadOfLogin(i: { search: string; inLineApp: boolean; hasLogin: boolean }): boolean {
  if (i.inLineApp || i.hasLogin) return false
  try {
    const sp = new URLSearchParams(i.search)
    if (['code', 'state', 'error', 'liff.state', 'access_token', 'id_token'].some((key) => sp.has(key))) return false
    if (sp.get('login') === '1') return false
    return true
  } catch {
    return false
  }
}

export type DockAction =
  | { kind: 'track'; label: string; href: string }
  | { kind: 'worship'; label: string; urls: string[] }
  | { kind: 'orders'; label: string; admin: boolean }

/**
 * ปุ่มทองในแถบล่างหน้าออร์เดอร์ (ไม่ใช่สถานะสร้างออร์เดอร์):
 * มีเลขพัสดุ → ติดตามพัสดุ · สำเร็จ/เสร็จพิธีและมีภาพวิธีบูชา → ดูวิธีบูชา · ถ้าเปิดหน้าออร์เดอร์ได้ → ดูออร์เดอร์ทั้งหมด · ไม่มี = null (เหลือปุ่ม LINE)
 */
export function resolveDockAction(i: {
  trackingUrl: string
  isDone: boolean
  worshipUrls: string[]
  ordersMode: 'owner' | 'admin' | null
}): DockAction | null {
  if (i.trackingUrl) return { kind: 'track', label: 'ติดตามพัสดุ', href: i.trackingUrl }
  if (i.isDone && i.worshipUrls.length > 0) return { kind: 'worship', label: 'ดูวิธีบูชา', urls: i.worshipUrls }
  if (i.ordersMode) {
    const admin = i.ordersMode === 'admin'
    return { kind: 'orders', label: admin ? 'ดูมุมมองลูกค้า' : 'ดูออร์เดอร์ทั้งหมดของฉัน', admin }
  }
  return null
}
